import config from '../../config';
import { Api, ApiConfig } from '../api';

// 响应类型定义
export interface BetaStatusResponse {
    betaAccess: boolean;
}

export interface RedeemCodeResponse {
    success: boolean;
    message?: string;
}

export interface ShopBinding {
    id: string;
    shopId: string;
    domain: string;
}

export interface ShopInfo {
    id: string;
    domain: string;
    brandName: string;
    platform: string;
    timezone: string;
    physicFlag: number;
}

export interface ShopifyConnectRequest {
    shop_domain: string;
    redirect_url: string;
}

export interface ShopifyConnectResponse {
    shop: string;
    authorizationUrl: string;
}

export interface ShopifyDisconnectRequest {
    shop_domain: string;
}

export interface ShopifyDisconnectResponse {
    success: boolean;
    message?: string;
}

export interface VerifyAuthResponse {
    id: string;
    email: string;
    userName: string;
    shopBindings: ShopBinding[];
    betaAccess: boolean;
    shopInfos: ShopInfo[];
}

export class AuthService {
    private api: Api;

    constructor(config: ApiConfig) {
        this.api = new Api(config);
    }

    /**
     * 验证用户身份
     */
    public async verifyAuth(): Promise<VerifyAuthResponse> {
        try {
            const response = await this.api.get<VerifyAuthResponse>('/api/auth/verify');
            return response;
        } catch (error) {
            throw error;
        }
    }

    /**
     * 获取用户的 beta 状态
     */
    public async getBetaStatus(): Promise<BetaStatusResponse> {
        try {
            const response = await this.api.get<BetaStatusResponse>('/api/beta/status');
            return response;
        } catch (error) {
            return { betaAccess: true };
        }
    }

    /**
     * 兑换邀请码
     * @param code 邀请码
     */
    public async redeemInviteCode(code: string): Promise<RedeemCodeResponse> {
        try {
            const response = await this.api.post<RedeemCodeResponse>('/api/beta/redeem', { code });
            return { success: true, message: response.message };
        } catch (error) {
            return { success: false, message: 'Verify code failed' };
        }
    }

    /**
     * 绑定 Shopify 店铺
     * @param accessToken Shopify Admin API access token
     * @param domain Shopify 店铺域名
     */
    public async bindShop({ accessToken, domain }: { accessToken: string, domain: string }): Promise<string> {
        try {
            const response = await this.api.post<string>('/api/shop-binding/bind', { accessToken, domain });
            return response;
        } catch (error) {
            console.error('Bind shop failed:', error);
            return 'fail';
        }
    }

    /**
     * Connect to Shopify after Google login
     * @param shopDomain Shopify shop domain
     * @param redirectUrl Redirect URL after connection
     * @param authorization Authorization token
     */
    public async connectShopify({ shop_domain,redirect_url }: ShopifyConnectRequest): Promise<ShopifyConnectResponse> {
        try {
            const response = await this.api.post<ShopifyConnectResponse>(
                '/api/shopify/connect',
                {
                    shop_domain: shop_domain,
                    redirect_url: redirect_url,
                    time_zone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                }
            );
            return response;
        } catch (error) {
            console.error('Connect Shopify failed:', error);
            throw error;
        }
    }

    /**
     * Disconnect from Shopify store
     * @param data Shopify disconnect data
     * @returns Promise with disconnect result
     */
    public async disconnectShopify({shop_domain}: ShopifyDisconnectRequest): Promise<ShopifyDisconnectResponse> {
        try {
            const response = await this.api.post<ShopifyDisconnectResponse>(`/api/shopify/disconnect?shop_domain=${shop_domain}`);
            return response;
        } catch (error) {
            console.error('Disconnect Shopify failed:', error);
            throw error;
        }
    }

    
}

// 创建 AuthService 实例
export const authService = new AuthService({
    baseURL: config.services.auth.apiHost,
    headers: {'Content-Type': 'application/json'}
});